import './BankConnections.scss'
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import moment from "moment"
import { apiErrorMessage, listBankConnections, listTricounts, searchAspsps, startBankConnection, syncBankConnection, syncTricount, updateBankAccount } from "../../ApiInterface"
import BankAccountRow from "./BankAccountRow"
import AccountsSettings from "./AccountsSettings"
import TricountsSettings, { TricountNotice } from "./TricountsSettings"
import { useAccounts } from "../../AccountsContext"

const DEFAULT_ASPSP_QUERY = 'Crédit Agricole'

const BankConnections = () => {
    const [bankConnections, setBankConnections] = useState([] as BankConnection[])
    const [aspsps, setAspsps] = useState([] as string[])
    const [aspspName, setAspspName] = useState('')
    const [message, setMessage] = useState('')
    // Erreur d'API affichée en haut de page, au lieu d'une promesse rejetée sans catch (overlay plein écran en dev)
    const [error, setError] = useState('')
    const [tricounts, setTricounts] = useState([] as Tricount[])
    const [tricountNotice, setTricountNotice] = useState({ message: '', error: '' } as TricountNotice)
    const [syncing, setSyncing] = useState(false)
    const [searchParams] = useSearchParams()
    const { reloadAccounts } = useAccounts()

    const fail = (fallback: string) => (e: any) => setError(apiErrorMessage(e, fallback))

    const fetchBankConnections = () => {
        listBankConnections().then(setBankConnections).catch(fail('Impossible de charger les connexions bancaires'))
    }

    const fetchTricounts = () => listTricounts().then(setTricounts).catch(fail('Impossible de charger les Tricount'))

    useEffect(fetchBankConnections, [])
    useEffect(() => { fetchTricounts() }, [])

    useEffect(() => {
        searchAspsps(DEFAULT_ASPSP_QUERY).then((names) => {
            setAspsps(names)
            setAspspName(names[0] || '')
        }).catch(fail('Impossible de charger la liste des banques'))
    }, [])

    const connect = (name: string) => {
        setError('')
        startBankConnection(name)
            .then((url) => { window.location.href = url })
            .catch(fail('La connexion à la banque a échoué'))
    }

    // « 1 nouvelle opération importée », « 3 nouvelles opérations importées »
    const imported = (count: number, noun: string) => count === 0
        ? `Aucune nouvelle ${noun} importée`
        : count > 1 ? `${count} nouvelles ${noun}s importées` : `1 nouvelle ${noun} importée`

    // Un seul bouton pour tout : les banques à l'accès valide, puis chaque Tricount importé.
    // Le résultat bancaire s'affiche en haut de page, celui des Tricount dans leur carte.
    const syncableConnections = bankConnections.filter((bankConnection) => bankConnection['valid_consent?'])
    const syncableTricounts = tricounts.filter((tricount) => tricount.enabled)

    const syncAll = () => {
        setSyncing(true)
        setError('')
        setMessage(syncableConnections.length > 0 ? 'Synchronisation...' : '')
        setTricountNotice({ message: syncableTricounts.length > 0 ? 'Synchronisation...' : '', error: '' })

        const banks = Promise.allSettled(syncableConnections.map((bankConnection) => syncBankConnection(bankConnection.id)))
            .then((results) => {
                if(syncableConnections.length === 0) {
                    return
                }
                const count = results.reduce((sum, result) => sum + (result.status === 'fulfilled' ? result.value.length : 0), 0)
                const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected')
                setMessage(failure ? '' : `${imported(count, 'opération')} depuis la banque`)
                if(failure) {
                    setError(`${apiErrorMessage(failure.reason, 'La synchronisation bancaire a échoué')}${count > 0 ? ` (${imported(count, 'opération')} quand même)` : ''}`)
                }
            })

        // L'un après l'autre : Tricount refuse deux sessions ouvertes en même temps (409).
        const tricountsSync = syncableTricounts
            .reduce((previous, tricount) => previous.then((results) => syncTricount(tricount.id).then(
                (value): PromiseSettledResult<BankTransaction[]>[] => [...results, { status: 'fulfilled', value }],
                (reason): PromiseSettledResult<BankTransaction[]>[] => [...results, { status: 'rejected', reason }])),
                Promise.resolve([] as PromiseSettledResult<BankTransaction[]>[]))
            .then((results) => {
                if(syncableTricounts.length === 0) {
                    return
                }
                const count = results.reduce((sum, result) => sum + (result.status === 'fulfilled' ? result.value.length : 0), 0)
                const errors = results.flatMap((result, i) => result.status === 'rejected'
                    ? [`« ${syncableTricounts[i].title} » : ${apiErrorMessage(result.reason, 'la synchronisation a échoué')}`]
                    : [])
                setTricountNotice({
                    message: imported(count, 'dépense'),
                    error: errors.join(' · '),
                })
            })

        Promise.all([banks, tricountsSync]).then(() => {
            setSyncing(false)
            fetchBankConnections()
            fetchTricounts()
            reloadAccounts()
        })
    }

    // Activer un compte bancaire crée son compte dans la navigation.
    const saveBankAccount = (id: number, params: BankAccountParams) => updateBankAccount(id, params).then(() => {
        fetchBankConnections()
        reloadAccounts()
    }).catch(fail('L\'enregistrement du compte a échoué'))

    return <div className="bank-connections">
        <div className="tl-page-head">
            <div>
                <h2>Banque</h2>
                <div className="sub">Import automatique des opérations depuis vos comptes et vos Tricount</div>
            </div>
            { (syncableConnections.length > 0 || syncableTricounts.length > 0) && <button
                className="tl-btn tl-btn-primary" disabled={syncing} onClick={syncAll}>
                { syncing ? 'Synchronisation...' : 'Tout synchroniser' }
            </button> }
        </div>

        { searchParams.get('error') && <div className="tl-alert tl-alert-danger">La connexion a échoué : { searchParams.get('error') }</div> }
        { error && <div className="tl-alert tl-alert-danger">{ error }</div> }
        { message && <div className="tl-alert">{ message }</div> }

        { bankConnections.map((bankConnection) => {
            const validConsent = bankConnection['valid_consent?']
            const expiresSoon = bankConnection['expires_soon?']

            return <div key={bankConnection.id} className="tl-card bank-connection">
                <div className="bank-connection-head">
                    <div>
                        <h3>{ bankConnection.aspsp_name }</h3>
                        <div className="status">
                            { validConsent
                                ? <span className={`tl-badge ${expiresSoon ? 'tl-badge-over' : 'tl-badge-ok'}`}>
                                    Accès valide jusqu'au { moment(bankConnection.valid_until).format('DD/MM/YYYY') }
                                </span>
                                : <span className="tl-badge tl-badge-over">Accès expiré</span> }
                            <span className="last-sync">
                                Dernière synchronisation : { bankConnection.last_synced_at ? moment(bankConnection.last_synced_at).format('DD/MM/YYYY HH:mm') : 'jamais' }
                            </span>
                        </div>
                    </div>
                    <div className="actions">
                        <button
                            className={`tl-btn ${validConsent && !expiresSoon ? 'tl-btn-secondary' : 'tl-btn-primary'}`}
                            onClick={() => connect(bankConnection.aspsp_name)}>
                            Renouveler l'accès
                        </button>
                    </div>
                </div>

                <table className="bank-accounts">
                    <thead>
                        <tr>
                            <th>Importer</th>
                            <th>Compte</th>
                            <th className="filing-column">Relevés</th>
                        </tr>
                    </thead>
                    <tbody>
                        { bankConnection.bank_accounts.map((bankAccount) => <BankAccountRow
                            key={bankAccount.id}
                            bankAccount={bankAccount}
                            onSave={(params) => saveBankAccount(bankAccount.id, params)}
                            onError={fail}
                        />) }
                    </tbody>
                </table>
            </div>
        }) }

        { bankConnections.length === 0 && <div className="tl-card connect-bank">
            <h3>Connecter une banque</h3>
            <div className="tl-field">
                <label htmlFor="aspsp-name">Banque</label>
                <select id="aspsp-name" className="tl-select" value={aspspName} onChange={(e) => setAspspName(e.target.value)}>
                    { aspsps.map((name) => <option key={name} value={name}>{name}</option>) }
                </select>
            </div>
            <button className="tl-btn tl-btn-primary" onClick={() => connect(aspspName)}>Connecter</button>
        </div> }

        <TricountsSettings tricounts={tricounts} reloadTricounts={fetchTricounts}
            notice={tricountNotice} setNotice={setTricountNotice}/>

        <AccountsSettings onError={fail}/>
    </div>
}

export default BankConnections
